import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays } from 'date-fns';
import { ja } from 'date-fns/locale';
import PageContainer from '../layout/PageContainer';

interface DashboardData {
  todaySales: number;
  todayTransactions: number;
  weekSales: number;
  monthSales: number;
  hourlyData: any[];
  categoryData: any[];
  paymentData: any[];
  dailyData: any[];
}

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month'>('today');
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [dateRange, selectedDate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const apiHost = window.location.hostname;
      
      // Fetch today's summary data (未締め分のみ)
      const todayResponse = await axios.get(`http://${apiHost}:3001/api/sales/summary`);
      const transactionsResponse = await axios.get(`http://${apiHost}:3001/api/transactions/today`);
      
      // 週間・月間データも取得
      const weeklyResponse = await axios.get(`http://${apiHost}:3001/api/sales/weekly`);
      const monthlyResponse = await axios.get(`http://${apiHost}:3001/api/sales/monthly`);
      
      // Process transactions for charts
      const transactions = transactionsResponse.data || [];
      
      // Create hourly data
      const hourlyData = Array.from({ length: 24 }, (_, hour) => {
        const hourTransactions = transactions.filter(t => {
          const transactionHour = new Date(t.timestamp).getHours();
          return transactionHour === hour;
        });
        return {
          hour: `${hour.toString().padStart(2, '0')}:00`,
          sales: hourTransactions.reduce((sum, t) => sum + (t.total || 0), 0),
          transactions: hourTransactions.length
        };
      }).filter(h => h.sales > 0); // Only show hours with sales
      
      // Create category data from items
      const categoryMap = new Map();
      transactions.forEach(t => {
        const items = typeof t.items === 'string' ? JSON.parse(t.items) : t.items || [];
        items.forEach(item => {
          const category = item.category || 'その他';
          const currentValue = categoryMap.get(category) || 0;
          categoryMap.set(category, currentValue + (item.price * item.quantity));
        });
      });
      
      const categoryData = Array.from(categoryMap.entries()).map(([name, value]) => ({
        name,
        value
      }));
      
      // Create payment data
      const paymentData = [
        {
          name: '現金',
          count: todayResponse.data.cash_sales > 0 ? Math.round(todayResponse.data.cash_sales / 1500) : 0, // Rough estimate
          amount: todayResponse.data.cash_sales || 0
        },
        {
          name: 'PayPay',
          count: todayResponse.data.paypay_sales > 0 ? Math.round(todayResponse.data.paypay_sales / 1500) : 0,
          amount: todayResponse.data.paypay_sales || 0
        }
      ].filter(p => p.amount > 0);

      setData({
        todaySales: todayResponse.data.total_sales || 0,
        todayTransactions: todayResponse.data.total_transactions || 0,
        weekSales: weeklyResponse.data.total_sales || 0,
        monthSales: monthlyResponse.data.total_sales || 0,
        hourlyData,
        categoryData,
        paymentData,
        dailyData: [{ // Today's data as single point
          date: format(new Date(), 'MM/dd'),
          sales: todayResponse.data.total_sales || 0,
          transactions: todayResponse.data.total_transactions || 0
        }]
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Fallback with empty data
      setData({
        todaySales: 0,
        todayTransactions: 0,
        weekSales: 0,
        monthSales: 0,
        hourlyData: [],
        categoryData: [],
        paymentData: [],
        dailyData: []
      });
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  // 日次締め処理
  const handleDailyClose = async () => {
    if (!confirm('本日の売上を締め処理しますか？\n締め結果を印刷します。\n一度実行すると取り消せません。')) {
      return;
    }
    
    try {
      const apiHost = window.location.hostname;
      const today = format(new Date(), 'yyyy-MM-dd');
      
      const response = await axios.post(`http://${apiHost}:3001/api/reports/daily-close`, {
        date: today
      });
      
      if (response.data) {
        // 締め処理成功メッセージ
        alert(`日次締め処理が完了しました。\n売上: ¥${response.data.summary?.totalSales?.toLocaleString() || 0}\n取引数: ${response.data.summary?.totalTransactions || 0}件\n\n続いて締め結果を印刷します。`);
        
        // 締め結果の印刷
        try {
          const printResponse = await axios.post(`http://${apiHost}:3001/api/receipt/print-closing-report`, {
            date: today,
            summary: response.data.summary,
            details: response.data.details || [],
            transactions: response.data.transactions || []
          });
          
          if (printResponse.data.success && printResponse.data.htmlUrl) {
            console.log('締め結果の印刷準備完了:', printResponse.data.htmlFile);
            
            // 新しいウィンドウで締め結果を開いて自動的に印刷ダイアログを表示
            const printWindow = window.open(
              `http://${apiHost}:3001${printResponse.data.htmlUrl}`,
              '_blank'
            );
            
            if (printWindow) {
              // ウィンドウが読み込まれたら印刷ダイアログを自動的に開く
              printWindow.onload = () => {
                setTimeout(() => {
                  printWindow.print();
                }, 500); // 少し待機してから印刷
              };
            } else {
              alert('ポップアップがブロックされました。\n手動で印刷画面を開いてください。');
            }
          } else {
            alert('締め結果の印刷に失敗しました。手動で印刷してください。');
          }
        } catch (printError) {
          console.error('Print error:', printError);
          alert('締め結果の印刷でエラーが発生しました。手動で印刷してください。');
        }
        
        fetchDashboardData(); // データを再読み込み
      }
    } catch (error) {
      console.error('Daily close error:', error);
      alert('日次締め処理でエラーが発生しました。');
    }
  };

  // CSV出力
  const handleCSVExport = async () => {
    try {
      const apiHost = window.location.hostname;
      const today = format(new Date(), 'yyyy-MM-dd');
      
      const response = await fetch(`http://${apiHost}:3001/api/reports/csv/daily/${today}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `daily_sales_${today}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        alert('CSV出力が完了しました。');
      } else {
        throw new Error('CSV export failed');
      }
    } catch (error) {
      console.error('CSV export error:', error);
      alert('CSV出力でエラーが発生しました。');
    }
  };

  // 会計ソフト連携用CSV出力
  const handleMoneyForwardExport = async () => {
    try {
      const apiHost = window.location.hostname;
      const today = format(new Date(), 'yyyy-MM-dd');
      
      const response = await fetch(`http://${apiHost}:3001/api/reports/csv/moneyforward/${today}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `moneyforward_${today}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        alert('会計ソフト連携用CSV出力が完了しました。');
      } else {
        throw new Error('MoneyForward export failed');
      }
    } catch (error) {
      console.error('MoneyForward export error:', error);
      alert('会計ソフト連携用CSV出力でエラーが発生しました。');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">ダッシュボードを読み込み中...</div>
        </div>
      </div>
    );
  }

  return (
    <PageContainer>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">売上ダッシュボード</h1>
          <p className="text-gray-600 mt-2">
            {format(new Date(), 'yyyy年MM月dd日 (E)', { locale: ja })} {format(new Date(), 'HH:mm')}
          </p>
        </div>

        {/* Date Range Selector */}
        <div className="mb-6 flex space-x-2">
          <button
            onClick={() => setDateRange('today')}
            className={`px-4 py-2 rounded-lg font-medium ${
              dateRange === 'today' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            今日
          </button>
          <button
            onClick={() => setDateRange('week')}
            className={`px-4 py-2 rounded-lg font-medium ${
              dateRange === 'week' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            今週
          </button>
          <button
            onClick={() => setDateRange('month')}
            className={`px-4 py-2 rounded-lg font-medium ${
              dateRange === 'month' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            今月
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">本日の売上</p>
                <p className="text-2xl font-bold text-gray-800 mt-2">
                  ¥{data?.todaySales?.toLocaleString() || 0}
                </p>
              </div>
              <div className="text-3xl">💰</div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">本日の取引数</p>
                <p className="text-2xl font-bold text-gray-800 mt-2">
                  {data?.todayTransactions || 0} 件
                </p>
              </div>
              <div className="text-3xl">📝</div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">今週の売上</p>
                <p className="text-2xl font-bold text-gray-800 mt-2">
                  ¥{data?.weekSales?.toLocaleString() || 0}
                </p>
              </div>
              <div className="text-3xl">📊</div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">今月の売上</p>
                <p className="text-2xl font-bold text-gray-800 mt-2">
                  ¥{data?.monthSales?.toLocaleString() || 0}
                </p>
              </div>
              <div className="text-3xl">📈</div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Hourly Sales Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">時間別売上</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data?.hourlyData || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip formatter={(value) => `¥${value?.toLocaleString()}`} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="sales" 
                  stroke="#3B82F6" 
                  name="売上"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Category Sales Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">カテゴリ別売上</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data?.categoryData || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {(data?.categoryData || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `¥${value?.toLocaleString()}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Daily Sales Trend */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">日別売上推移</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data?.dailyData || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip formatter={(value) => `¥${value?.toLocaleString()}`} />
                <Legend />
                <Bar dataKey="sales" fill="#10B981" name="売上" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Payment Method Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">決済方法別</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data?.paymentData || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}件`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {(data?.paymentData || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">クイックアクション</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button 
              onClick={handleDailyClose}
              className="p-4 bg-red-50 hover:bg-red-100 rounded-lg text-center transition-colors group"
            >
              <div className="text-2xl mb-2">🔒</div>
              <div className="text-sm font-medium text-gray-700 group-hover:text-red-700">日次締め処理</div>
              <div className="text-xs text-gray-500 mt-1">未締め分のみ</div>
            </button>
            <button 
              onClick={handleCSVExport}
              className="p-4 bg-green-50 hover:bg-green-100 rounded-lg text-center transition-colors group"
            >
              <div className="text-2xl mb-2">💾</div>
              <div className="text-sm font-medium text-gray-700 group-hover:text-green-700">CSVエクスポート</div>
            </button>
            <button 
              onClick={handleMoneyForwardExport}
              className="p-4 bg-blue-50 hover:bg-blue-100 rounded-lg text-center transition-colors group"
            >
              <div className="text-2xl mb-2">📊</div>
              <div className="text-sm font-medium text-gray-700 group-hover:text-blue-700">会計ソフト連携</div>
            </button>
            <button 
              onClick={fetchDashboardData}
              className="p-4 bg-purple-50 hover:bg-purple-100 rounded-lg text-center transition-colors group"
            >
              <div className="text-2xl mb-2">🔄</div>
              <div className="text-sm font-medium text-gray-700 group-hover:text-purple-700">データ更新</div>
            </button>
          </div>
        </div>
    </PageContainer>
  );
};

export default Dashboard;